import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Check, Code2, FileCode2, RotateCcw, Save, ShieldCheck, X } from 'lucide-react';

import { cn } from '@/lib/utils';

import type { DeveloperDiagnostic } from '../types';

type DeveloperTab = 'html' | 'pageCss' | 'css';

interface DeveloperPanelProps {
  html: string;
  css: string;
  pageCss: string;
  dirty: boolean;
  diagnostics: DeveloperDiagnostic[];
  status: 'idle' | 'loading' | 'applying' | 'applied' | 'error';
  onChangeHtml: (value: string) => void;
  onChangeCss: (value: string) => void;
  onChangePageCss: (value: string) => void;
  onApply: () => void;
  onReset: () => void;
  onClose: () => void;
}

export function DeveloperPanel({ html, css, pageCss, dirty, diagnostics, status, onChangeHtml, onChangeCss, onChangePageCss, onApply, onReset, onClose }: DeveloperPanelProps) {
  const [tab, setTab] = useState<DeveloperTab>('html');
  const value = tab === 'html' ? html : tab === 'pageCss' ? pageCss : css;
  const fieldDiagnostics = tab === 'html' ? diagnostics.filter((item) => item.field === 'html') : diagnostics.filter((item) => item.field === 'css');
  const busy = status === 'loading' || status === 'applying';

  return (
    <aside className="flex h-full w-full flex-col border-l border-slate-200 bg-white" data-testid="developer-panel">
      <div className="border-b border-slate-100 px-5 pb-4 pt-4">
        <div className="flex items-start justify-between gap-3">
          <div>
            <div className="flex items-center gap-2 text-xs font-bold uppercase tracking-[0.16em] text-violet-700">
              <Code2 className="h-4 w-4" /> Developer
            </div>
            <h2 className="mt-2 text-base font-bold text-slate-950">Code view</h2>
            <p className="mt-1 text-xs leading-relaxed text-slate-500">Werk met veilige HTML en scoped CSS. De Visual Builder blijft de structurele bron.</p>
          </div>
          <button type="button" onClick={onClose} aria-label="Developer Mode sluiten" className="rounded-lg p-1.5 text-slate-400 transition hover:bg-slate-100 hover:text-slate-700">
            <X className="h-4 w-4" />
          </button>
        </div>
        <div className="mt-4 flex items-center gap-2 rounded-xl bg-emerald-50 px-3 py-2 text-xs leading-relaxed text-emerald-800">
          <ShieldCheck className="h-4 w-4 shrink-0" />
          Scripts, externe imports en onveilige selectors worden geblokkeerd.
        </div>
      </div>

      <div className="flex border-b border-slate-100 px-5 pt-3">
        {(['html', 'pageCss', 'css'] as const).map((entry) => (
          <button key={entry} type="button" onClick={() => setTab(entry)} className={cn('border-b-2 px-3 pb-2.5 text-xs font-bold uppercase tracking-[0.12em]', tab === entry ? 'border-violet-600 text-violet-800' : 'border-transparent text-slate-500')}>
            {entry === 'html' ? 'HTML' : entry === 'pageCss' ? 'Pagina-CSS' : 'Custom CSS'}
          </button>
        ))}
      </div>

      <div className="min-h-0 flex-1 p-4">
        <CodeEditor
          language={tab === 'html' ? 'html' : 'css'}
          value={value}
          disabled={busy}
          diagnostics={fieldDiagnostics}
          onChange={tab === 'html' ? onChangeHtml : tab === 'pageCss' ? onChangePageCss : onChangeCss}
        />
      </div>

      <div className="border-t border-slate-100 px-5 py-4">
        {diagnostics.length > 0 ? (
          <div className="mb-3 space-y-1.5" role="alert">
            {diagnostics.map((diagnostic, index) => (
              <div key={`${diagnostic.field}-${diagnostic.line}-${index}`} className={cn('rounded-lg px-3 py-2 text-xs leading-relaxed', diagnostic.severity === 'error' ? 'bg-red-50 text-red-800' : 'bg-amber-50 text-amber-800')}>
                <strong>{diagnostic.field.toUpperCase()} {diagnostic.line}:{diagnostic.column}</strong> {diagnostic.message}
              </div>
            ))}
          </div>
        ) : null}
        <div className="flex items-center justify-between gap-3">
            <span className={dirty ? 'text-xs font-semibold text-amber-700' : 'text-xs text-slate-500'}>
              {dirty ? 'Onopgeslagen wijzigingen' : status === 'loading' ? 'Code ophalen…' : status === 'applying' ? 'Wijzigingen toepassen…' : status === 'applied' ? 'Toegepast op de canonical content.' : status === 'error' ? 'Controleer de foutmelding.' : 'Geen wijzigingen'}
          </span>
          <div className="flex gap-2">
            <button type="button" onClick={onReset} disabled={busy} className="inline-flex items-center gap-1.5 rounded-lg border border-slate-200 px-3 py-2 text-xs font-semibold text-slate-700 disabled:opacity-50">
              <RotateCcw className="h-3.5 w-3.5" /> Reset
            </button>
            <button type="button" onClick={onApply} disabled={busy || !dirty} className="inline-flex items-center gap-1.5 rounded-lg bg-violet-700 px-3 py-2 text-xs font-semibold text-white shadow-sm disabled:opacity-50">
              {status === 'applied' ? <Check className="h-3.5 w-3.5" /> : <Save className="h-3.5 w-3.5" />}
              Apply
            </button>
          </div>
        </div>
      </div>
    </aside>
  );
}

function CodeEditor({ language, value, disabled, diagnostics, onChange }: { language: DeveloperTab; value: string; disabled: boolean; diagnostics: DeveloperDiagnostic[]; onChange: (value: string) => void }) {
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const codeRef = useRef<HTMLElement>(null);
  const gutterRef = useRef<HTMLDivElement>(null);
  const lineCount = Math.max(value.split('\n').length, 1);
  const highlighted = useMemo(() => highlightCode(value, language), [value, language]);

  useEffect(() => {
    const textarea = textareaRef.current;
    const code = codeRef.current;
    const gutter = gutterRef.current;
    if (!textarea || !code || !gutter) return;
    const sync = () => {
      code.style.transform = `translate(${-textarea.scrollLeft}px, ${-textarea.scrollTop}px)`;
      gutter.scrollTop = textarea.scrollTop;
    };
    textarea.addEventListener('scroll', sync);
    sync();
    return () => textarea.removeEventListener('scroll', sync);
  }, []);

  const firstErrorLine = diagnostics.find((item) => item.severity === 'error')?.line;

  return (
    <div className="flex h-full min-h-[26rem] flex-col overflow-hidden rounded-xl border border-slate-300 bg-slate-950 shadow-inner">
      <div className="flex items-center justify-between border-b border-white/10 px-3 py-2 text-[11px] font-semibold text-slate-400">
        <span className="flex items-center gap-2"><FileCode2 className="h-3.5 w-3.5" />{language.toUpperCase()}</span>
        <span>{lineCount} regels</span>
      </div>
      <div className="relative min-h-0 flex-1 overflow-hidden font-mono text-[12px] leading-6">
        <div ref={gutterRef} aria-hidden="true" className="absolute inset-y-0 left-0 z-10 w-12 overflow-hidden border-r border-white/10 bg-slate-900 px-2 pt-3 text-right text-slate-600">
          {Array.from({ length: lineCount }, (_, index) => <div key={index} className={firstErrorLine === index + 1 ? 'font-bold text-red-400' : undefined}>{index + 1}</div>)}
        </div>
        <pre aria-hidden="true" className="pointer-events-none absolute inset-0 overflow-hidden pl-[4.5rem] pr-3 pt-3 text-slate-200"><code ref={codeRef} dangerouslySetInnerHTML={{ __html: highlighted }} /></pre>
        <textarea
          ref={textareaRef}
          value={value}
          disabled={disabled}
          onChange={(event) => onChange(event.target.value)}
          spellCheck={false}
          aria-label={`${language.toUpperCase()} code`}
          className="absolute inset-0 resize-none overflow-auto bg-transparent pl-[4.5rem] pr-3 pt-3 text-transparent caret-white outline-none selection:bg-violet-500/40 disabled:cursor-wait"
        />
      </div>
    </div>
  );
}

function highlightCode(value: string, language: DeveloperTab): string {
  const escaped = value.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');
  if (language === 'html') {
    return escaped
      .replace(/(&lt;\/?)([a-z0-9-]+)/gi, '$1<span class="text-pink-300">$2</span>')
      .replace(/([a-z-:]+)(=)(&quot;.*?&quot;|".*?"|'.*?')/gi, '<span class="text-amber-300">$1</span>$2<span class="text-emerald-300">$3</span>');
  }
  return escaped
    .replace(/(^|\n)(\s*)([.#a-z0-9_-]+)(\s*\{)/gi, '$1$2<span class="text-violet-300">$3</span>$4')
    .replace(/([a-z-]+)(\s*:)/gi, '<span class="text-sky-300">$1</span>$2');
}
